import React, { useState } from 'react';
import './Publications.css';
import { useLanguage } from '../../contexts/LanguageContext';
import { useContent } from '../../contexts/ContentContext';

type PublicationType = 'international' | 'domestic' | 'journal';
type Language = 'jp' | 'en';

const Publications: React.FC = () => {
  const [category, setCategory] = useState<'all' | PublicationType>('all');
  const { language } = useLanguage();
  const { content, isLoading } = useContent();

  if (isLoading || !content) return null;

  // Sort by ID descending (newest first)
  const publications = [...content.publications.list].sort((a, b) => 
    parseInt(b.id) - parseInt(a.id)
  );
  const filteredPublications = category === 'all'
    ? publications
    : publications.filter(pub => pub.type === category);

  const getCategoryTag = (type: PublicationType) => {
    const tags = {
      international: 'international-tag',
      domestic: 'domestic-tag',
      journal: 'journal-tag'
    } as const;
    return tags[type];
  };

  const getCategoryText = (type: PublicationType): string => {
    const texts = content.publications.categories as Record<Language, Record<PublicationType | 'all', string>>;
    return texts[language][type];
  };

  const allText = content.publications.categories[language].all;

  return (
    <section id="publications" className="section">
      <h2 className="section-title">{language === 'jp' ? 'Publications' : 'Publications'}</h2>
      
      <div className="publication-categories">
        <button 
          className={`category-btn ${category === 'all' ? 'active' : ''}`}
          onClick={() => setCategory('all')}
        >
          {allText}
        </button>
        <button 
          className={`category-btn ${category === 'international' ? 'active' : ''}`}
          onClick={() => setCategory('international')}
        >
          {getCategoryText('international')}
        </button>
        <button 
          className={`category-btn ${category === 'domestic' ? 'active' : ''}`}
          onClick={() => setCategory('domestic')}
        >
          {getCategoryText('domestic')}
        </button>
        <button 
          className={`category-btn ${category === 'journal' ? 'active' : ''}`}
          onClick={() => setCategory('journal')}
        >
          {getCategoryText('journal')}
        </button>
      </div>

      <div className="publications-grid">
        {filteredPublications.map((pub) => (
          <article key={pub.id} className="publication-card fade-in" data-type={pub.type}>
            <div className="publication-content">
              <div className="publication-header">
                <div className={`publication-type-tag ${getCategoryTag(pub.type)}`}>
                  {getCategoryText(pub.type)}
                </div>
                {pub.isNew && <span className="new-badge">{language === 'jp' ? '新着' : 'New'}</span>}
              </div>
              <h3 className="publication-title">{pub.title[language]}</h3>
              <p className="publication-authors">
                {pub.authors.map((author, index) => (
                  <span key={index} className={author.includes('大坂 洋豊') ? 'author-highlight' : ''}>
                    {author}
                    {index < pub.authors.length - 1 ? '、' : ''}
                  </span>
                ))}
              </p>
              <p className="publication-conference">{pub.conference[language]}</p>
              <div className="publication-meta">
                <div className="publication-year">{pub.year}</div>
                <div className="publication-keywords">
                  {pub.keywords[language].map((keyword: string) => (
                    <span key={keyword} className="keyword-tag">{keyword}</span>
                  ))}
                </div>
              </div>
              {pub.links && pub.links.length > 0 && (
                <div className="publication-links">
                  {pub.links.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pub-link"
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default Publications; 